import { ColumnDef } from '@tanstack/react-table'
import { TextCell } from 'components/table/cells/TextCell'
import { createEditColumn } from 'components/table/enhancements/CreateEditColumn'
import { Expandable } from 'components/table/utility/expandableTable'
import styled from 'styled-components'
import {
  GeckoSite,
  GeckoSiteGrouping,
  InputType,
  PickOptional,
  PickRequired,
} from 'types'

const RadioInput = styled.input`
  cursor: pointer;
  width: 1rem;
  height: 1rem;
`

type CommonColumnConfig = {
  id: string
  header: string
}

type RequiredColumnConfig = {
  accessorKey: keyof PickRequired<GeckoSiteGrouping>
  placeholder: undefined
} & CommonColumnConfig

type OptionalColumnConfig = {
  accessorKey: keyof PickOptional<GeckoSiteGrouping>
  placeholder: string
} & CommonColumnConfig

type ColumnConfig = RequiredColumnConfig | OptionalColumnConfig

const columnConfigs: ColumnConfig[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    placeholder: undefined,
  },
  {
    id: 'category',
    accessorKey: 'category',
    header: 'Category',
    placeholder: '-',
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: 'Description',
    placeholder: undefined,
  },
  {
    id: 'id',
    accessorKey: 'id',
    header: 'Grouping ID',
    placeholder: undefined,
  },
]

export function groupingsToFlatArray(
  groupings: GeckoSite['data']['groupings']
): GeckoSiteGrouping[] {
  return Object.entries(groupings).map(([id, grouping]) => ({
    id,
    name: grouping.name,
    category: grouping.category,
    description: grouping.description,
    parent: grouping.parent,
  }))
}

export const groupingsTableColumns = (
  onEditRow: (row: Expandable<GeckoSiteGrouping>) => void,
  selectionMode?: boolean,
  selectedGroupingId?: string,
  onSelectGrouping?: (groupingId: string) => void
): ColumnDef<Expandable<GeckoSiteGrouping>>[] => {
  const columns: ColumnDef<Expandable<GeckoSiteGrouping>>[] = columnConfigs.map(
    ({ id, accessorKey, header, placeholder }) => ({
      id,
      accessorKey,
      header,
      filterFn: 'includesString',
      sortingFn: 'alphanumeric',
      sortUndefined: 'last',
      meta: {
        inputType: InputType.Text,
      },
      cell: ({ row }) => {
        return (
          <TextCell text={row.original[accessorKey]} emptyText={placeholder} />
        )
      },
    })
  )

  if (selectionMode) {
    const radioColumn: ColumnDef<Expandable<GeckoSiteGrouping>> = {
      id: 'select',
      header: '',
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => (
        <RadioInput
          type='radio'
          name='grouping-selection'
          checked={selectedGroupingId === row.original.id}
          onChange={() => onSelectGrouping?.(row.original.id)}
        />
      ),
    }
    return [radioColumn, ...columns, createEditColumn(onEditRow)]
  }

  return [...columns, createEditColumn(onEditRow)]
}
